import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BentoProfileContainer } from '@/components/bento-profile/BentoProfileContainer';
import { useAuth } from '@/contexts/AuthContext';
import { Profile } from '@/types/auth';

const BentoProfile = () => {
  const { username } = useParams();
  const { user } = useAuth();

  // Fetch profile by username
  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery({
    queryKey: ['bento-profile', username],
    queryFn: async () => {
      if (!username) throw new Error('Username is required');
      
      const { data, error } = await supabase
        .from('public_profiles')
        .select('*')
        .eq('username', username)
        .single();

      if (error) throw error;
      return data as Profile;
    },
    enabled: !!username,
  });

  // Fetch bento config
  const { data: bentoConfig, isLoading: configLoading } = useQuery({
    queryKey: ['bento-config', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      
      const { data, error } = await supabase
        .from('profile_2_configs')
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id,
  });

  if (profileLoading || configLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (profileError || !profile) {
    return <Navigate to="/404" replace />;
  }

  // Check if bento profile is enabled, if not redirect to regular profile
  if (!bentoConfig?.is_enabled) {
    return <Navigate to={`/profile/${username}`} replace />;
  }

  const isOwnProfile = user?.id === profile.id;

  return (
    <div className="min-h-screen bg-background">
      <BentoProfileContainer
        profile={profile}
        bentoConfig={bentoConfig}
        isOwnProfile={isOwnProfile}
      />
    </div>
  );
};

export default BentoProfile;